/**
 * Browser client for the server-owned dancer session.
 *
 * Dancer credentials never enter Firebase Auth. The server sets an opaque,
 * HttpOnly cookie and this client sends it only to the two dancer endpoints.
 * No student id, dancer uid, or acknowledgement identity is accepted from the
 * browser: the server derives all authority from the validated cookie.
 */

import {
  DANCER_PREVIEW_BLOCKED_NOTE,
  blockedByDancerPreview,
  dancerPreviewActive,
} from './dancerPreviewGuard.ts';
import type { ClassTechniqueResult } from './techniqueProgram.ts';
import { classInHandFromWire, inHandFrom, type ClassInHandCostume, type CostumeInHand, type FamilyPiece } from './costumeInHand.ts';
import { formationsViewFrom, type ClassFormationsView } from './classFormationsClient.ts';
import { trackerFromWire, type TrackerPayload } from './costumeTracker.ts';
import { parseBagGroupView, type BagGroupView } from './bagGroupViewData.ts';
import { parseDancerEventPage, type DancerEventPage } from './dancerEventPage.ts';
import {
  parseAssessmentDetail,
  parseAssessmentSummary,
  type AssessmentDetail,
  type AssessmentSummary,
} from './assessments.ts';

export const DANCER_AUTH_ENDPOINT = '/.netlify/functions/dancerAuth';
export const DANCER_PORTAL_ENDPOINT = '/.netlify/functions/dancerPortal';
export const DANCER_CSRF_HEADER = 'X-Dancer-CSRF';
export type DancerExperienceMode = 'younger' | 'older';

export interface DancerAuthStatus {
  authenticated: boolean;
  csrfToken: string | null;
  /** Present only when the browser supplied a dancer cookie that is no longer valid. */
  reason: 'session_ended' | null;
  /**
   * JR STAFF PARITY (Bobbie, 2026-08-19 and again 2026-08-21: a designated
   * dancer needs "the exact same access as someone who created a junior
   * staff account who is also a dancer").
   *
   * Present ONLY for a dancer the Director has designated Jr Staff, and only
   * while the server's parity flag is on. It is a Firebase custom token for
   * the SAME uid the dancer cookie already authenticates, which the browser
   * exchanges for a real Auth session so the rule-gated staff surfaces open.
   * Absent for every ordinary dancer, which is still the overwhelming
   * majority: their sign-in remains cookie-only and touches Firebase Auth
   * not at all. See netlify/functions/dancerAuth.mjs for what the token may
   * and may not carry.
   */
  jrStaffToken: string | null;
}

export interface DancerPortalClass {
  id: string;
  name: string;
  kind: string;
  style: string;
  level: string;
  teacherName: string | null;
  /**
   * Jr assistants attached to this class, in the same family-safe shape as
   * offering.assistantUids / offering.assistantNames (src/lib/offerings.ts):
   * a capped uid list and a display-name snapshot keyed by uid. Built
   * server-side in netlify/functions/dancerPortal.mjs, which is why there is
   * no live-account fallback here the way staff surfaces have one - a
   * dancer session never gets a users/{uid} lookup, so the snapshot IS the
   * name. The server has already reduced every one of these to a first name
   * (dancerPortalTeacherLabel), so no surname and no email-shaped string
   * reaches this session at all, and a uid the studio cannot name yet
   * carries the honest "Someone" placeholder rather than being dropped -
   * #828's policy, the same one the teacherName line above follows. A raw
   * uid is never rendered as a substitute for a name.
   */
  assistantUids?: string[];
  assistantNames?: Record<string, string>;
  assistantDistinctUids?: string[];
  isCompetition: boolean;
  /**
   * WHAT SHE DOES ON THIS CLASS, when she is Jr Staff on it: she is named on
   * its teaching team, or she is one of its Jr assistants. Absent on an
   * ordinary class she only dances in, and absent from every payload for a
   * dancer who is not Jr Staff at all.
   *
   * Bobbie, 2026-09-07 evening: *"it should all be blended in with her
   * classes so when she clicks on my classes, it's just her list of all of
   * the classes she dance dances and also the list of classes that she
   * teaches in with a pill that says junior assistant or junior staff."* So
   * there is no second list any more; there is this field, and the pill the
   * screens draw from it. Teaching wins when she is both, the same tiebreak
   * familyClassSet.ts and the server's own jrStaffOfferingRole use.
   */
  role?: 'teaching' | 'assisting';
  /**
   * True when she is ENROLLED in this class as well as staffing it. The pill
   * still says Teaching (Bobbie's own reading), but the row keeps every
   * dancer-side thing her enrollment earns her: the board, the roster, the
   * costume card.
   */
  dancing?: boolean;
  /**
   * True for a class she ONLY teaches or assists. `roster`, `costumes` and
   * `posts` are empty on such a row because she is not enrolled in it, NOT
   * because the class is empty - so a screen must read this flag and leave
   * those panels out rather than printing "no other dancers are listed for
   * this dance", which would be a false claim about a real roster. Her
   * staff-side view of that class (attendance, the full roster) is on the
   * staff pages, not here.
   */
  jrStaffOnly?: boolean;
  /**
   * True when she is on this class ONLY through a temporary sub seat: it is
   * open to her for the sub window, but it is not one of her weekly classes
   * (feedback gxpVbdu6). Sent by blendJrStaffClasses in
   * netlify/functions/dancerPortal.mjs; her email sign-in's list carries the
   * same field (lib/jrStaffClassRole.ts, BlendedStaffClass). Screens say it
   * with lib/subCoverLabel.ts, never "You teach this class."
   */
  covering?: true;
  /** The studio dates (YYYY-MM-DD) she covers on a `covering` row, sorted. */
  coveredDates?: string[];
  /**
   * The single letter this CLASS may honestly wear, when every one of its
   * upcoming meetings falls on a marked week and all on the same one. An
   * ordinary every-week class hits both A and B weeks and correctly carries
   * nothing. Same rule the family class list uses (uniformWeekPattern in
   * lib/abWeekCalendar.ts), so a dancer and their parent read the same letter.
   */
  weekPattern?: 'A' | 'B';
  compLookShoes: string | null;
  media: DancerPortalMedia[];
  roster: DancerPortalRosterMember[];
  /**
   * True when the server's read caps cut the roster short. A partial list that
   * admits it is partial, rather than a short list presented as the whole
   * class - see the roster budget in netlify/functions/dancerPortal.mjs.
   */
  rosterHasMore?: boolean;
  costumes: DancerPortalCostume[];
  costumesHasMore: boolean;
  posts: DancerPortalClassPost[];
  postsHasMore: boolean;
  /**
   * The class's confirmed comp eval date (compEvalDates/{offeringId}), when
   * one is set. Sent by netlify/functions/dancerPortal.mjs; absent means no
   * date, or the read failed (the pill then simply does not show).
   */
  compEval?: { date: string; startTime: string; endTime: string };
}

export interface DancerPortalMedia {
  id: string;
  kind: 'music' | 'video';
  label: string;
  url: string;
  category: 'class' | 'recital' | 'comp' | null;
  finalCut: boolean;
  /** The song's artist, music only; absent when none was entered. */
  artist?: string;
  /**
   * When the studio added this link, epoch ms, 0 for links written before the
   * field existed. The portal sorts newest first on the way out, so a screen
   * can lead with "most recent" without re-deriving it; this is here so a
   * screen can also SAY when, and so a test can prove the order.
   */
  addedAt?: number;
  /**
   * WHO POSTED IT, VIDEOS ONLY, as one display label the server already made
   * safe: a teacher by first name with her title ("Ms. Dallas"), a dancer by
   * first name and last initial ("Nova T."), a parent by full name and "class
   * parent" ("Jane Smith, class parent", or "a class parent" when her name
   * could not be used; Bobbie's ruling, DECISIONS 116), and a Jr Staff the same
   * on either sign-in (as the class header names her on a class she staffs, as
   * a dancer on one she only dances in). Never a uid or an email, and never a
   * teacher's or a dancer's surname, because none of those is on the wire at
   * all (netlify/functions/lib/mediaPostedBy.mjs decides it). ABSENT, not
   * blank, on every song and whenever the server could not derive a label it
   * was willing to send, and a screen must say nothing about who in that case
   * rather than make one up. Read through dancerMediaPostedLine
   * (lib/dancerMediaPosted.ts), which pairs it with addedAt.
   */
  addedByLabel?: string;
  /**
   * The type the studio recorded for the file, when it has one. Decides the
   * extension a saved video lands under; without it videoFileName falls back
   * to .mp4 and a stored .mov saves under the wrong name.
   */
  contentType?: string;
  /**
   * The name the studio uploaded this under. Decides the extension a saved
   * file lands under, ahead of contentType - and it is the ONLY one of the two
   * that a class music upload records, so without it every non-mp3 song a
   * dancer saves would be named ".mp3".
   */
  fileName?: string;
  /**
   * R2 home (DECISIONS.md, "Cloudflare R2 is the video home"). Absent means
   * GCS - `url` is directly playable, exactly today's shape. 'r2' means
   * `url` is a placeholder only; resolve the real playable link through
   * hub/src/lib/videoPlayback.ts, the same helper the family/staff surfaces
   * use, with the dancer's own principal (no familyStudentId needed).
   */
  storage?: 'r2';
  r2Key?: string;
  /**
   * The transcoded 1080p rendition's R2 key, when the migration sweep has
   * copied one across. videoPlayback.ts preferred it over r2Key until the
   * 2026-09-18 playback hold (PLAYBACK_HOLD_REASON there): under the hold a
   * dancer plays the original, and this key is carried so the copy is ready
   * the moment the hold lifts. Absent means there is no copy yet.
   */
  r2StreamKey?: string;
  /**
   * A GCS video's 1080p copy, sent only when it differs from url. The player
   * falls back to it when this device cannot play the original
   * (videoPlayback.ts playableCopy).
   */
  streamUrl?: string;
}

export interface DancerPortalRosterMember {
  id: string;
  displayName: string;
  isMe: boolean;
}

export interface DancerPortalCostume {
  id: string;
  name: string;
  imageUrl: string | null;
  danceTrackLabel: string | null;
  extrasRequired: string[];
  shoeColor: string | null;
  shoeType: string | null;
  tights: string | null;
  provisionType: 'not-set' | 'family-supplied' | 'studio-ordering';
  orderStatus: 'not-started' | 'selected' | 'ordered' | 'arrived' | 'picked-up';
  readyForPickup: boolean;
  /**
   * THIS dancer's own pickup, never the class's (2026-09-26). The server
   * reads costumes/{id}.pickups for her id alone and sends only the answer;
   * the pickups map itself never crosses to the dancer side. Optional so an
   * older server reply still renders (DancerCostumeCard falls back).
   */
  pickupState?: 'picked-up' | 'ready' | 'not-ready' | 'family-provides' | 'check-with-studio';
  /** When her own bag was marked, ISO; null when not picked up or unknown. */
  pickedUpAt?: string | null;
  /**
   * TRIED ON AND HER PIECES (2026-11-02). What the card asks the family to
   * bring, whether Tried on is open yet, and her own marks with names and
   * times (src/lib/costumeInHand.ts). Optional so an older server reply
   * still renders without the checklist.
   */
  pieces?: FamilyPiece[];
  triedOnOpen?: boolean;
  inHand?: CostumeInHand;
}

/**
 * One of Kim's important dates, as a dancer sees it.
 *
 * Bobbie, 2026-08-28: *"the countdowns to important dates"*. Assembled by
 * netlify/functions/dancerPortal.mjs from `countdownEvents`, which a dancer
 * session cannot read for itself. `visibility` and `rsvps` never cross that
 * boundary - see readCountdowns' own doc comment for what is stripped and why.
 */
export interface DancerPortalCountdown {
  countdownEnabled?: boolean;
  /** Validated studio wall-clock values from the saved eventV2 map. */
  startTime?: string;
  endTime?: string;
  allDay?: boolean;
  feeDueDate?: string;
  amountDueNote?: string;
  rsvpRequired?: boolean;
  arriveMinutesBefore?: number;
  id: string;
  name: string;
  /** A single emoji, or empty when Kim did not set one. */
  emoji: string;
  /** '#RRGGBB', or empty. */
  color: string;
  /** 'YYYY-MM-DD'. The START of the event when it spans days. */
  date: string;
  /** 'YYYY-MM-DD', never earlier than `date`. Absent = a single-day event. */
  endDate?: string;
  kind: 'competition' | 'recital' | 'other';
  details?: string;
  venue?: string;
  /** Kim's "show this on every class inside the range". Absent = false. */
  showOnClasses?: boolean;
  /**
   * The early door, when Kim set one. Absent means always-show. Present means
   * the window is already OPEN - the portal drops any date whose window has
   * not started - and is carried only so the class-pill rule can tell the two
   * apart. See classPillEvents in lib/dancerEventPills.ts.
   */
  startShowingDaysBefore?: number;
}

export interface DancerPortalEvent {
  id: string;
  offeringId: string;
  title: string;
  roomId: string;
  kind: 'class' | 'rehearsal' | 'event';
  startAt: string;
  endAt: string;
  /**
   * Which marked rehearsal week this date falls in, or absent when the
   * Director has not marked that week - which is the normal state for most of
   * the year, and is why this is optional rather than nullable-required.
   * Computed server-side in STUDIO time (netlify/functions/dancerPortal.mjs):
   * a dancer session cannot read studioSettings/abWeekCalendar itself, and a
   * UTC server resolving the date would file a Sunday-night class in the wrong
   * week.
   */
  weekPattern?: 'A' | 'B';
  /**
   * Present only on a meeting of a class she TEACHES or ASSISTS.
   *
   * NO SCREEN READS THIS, and the comment that used to say the calendar did
   * was wrong. Every dancer surface takes its pill from
   * jrStaffRoleByOfferingId over portal.classes instead, on purpose: a class
   * she both dances in and teaches has meetings arriving from the ENROLLED
   * lane with no role on them at all, so a per-event tag would have been
   * right on some of that class's rows and missing on the rest. One map, off
   * the one class list, cannot do that.
   *
   * Kept rather than dropped because it is what the server actually knows
   * when it appends a meeting, it costs one word on the wire, and the
   * blend's own tests read it to prove a meeting came from the lane they
   * think it did. If a future surface wants the seat for an event, the class
   * map is still the answer; this is provenance, not a display field.
   */
  role?: 'teaching' | 'assisting';
}

export type DancerAbsenceReason = 'sick' | 'travel' | 'school' | 'family' | 'other';

export interface DancerPortalClassPost {
  id: string;
  authorName: string;
  authorType: 'dancer' | 'family' | 'studio';
  body: string;
  createdAt: string;
  videoUrl?: string;
  attachments?: Array<{
    id: string;
    kind: 'image' | 'video' | 'file';
    url: string;
    name: string;
    contentType: string;
    storagePath: string;
  }>;
  /**
   * This dancer's OWN current reaction, if any. Never a reply, never an
   * acknowledgement - reactToDancerPortalClassPost below writes only
   * classPosts.dancerReactions, its own field, so setting or clearing this
   * can never satisfy the family's response to the post.
   */
  myReaction?: string;
  /**
   * Per-emoji counts across every DANCER who reacted (classPosts.dancerReactions).
   * Never includes a family or staff reaction - those live in a separate
   * field this portal does not read.
   */
  reactionCounts?: Record<string, number>;
  /**
   * True on a post SHE wrote, on whichever of her sign-ins (the server
   * decides, through the shared alias helper; no uid ever reaches this
   * browser). An older dancer gets Edit and Delete on it (Bobbie, 2026-09-04:
   * "We need to set it so anyone can edit their own posts and delete their
   * own posts").
   */
  mine?: boolean;
  /**
   * True on her own post that asked families to acknowledge it once
   * somebody has: its words are locked, Delete still works. Only ever set
   * together with `mine`.
   */
  editLocked?: boolean;
}

export interface DancerPortalTodo {
  id: string;
  title: string;
  detail: string;
  dueOn: string;
  done: boolean;
  doneAt: string | null;
  doneByName: string | null;
  createdByName: string | null;
  createdAt: string;
  /** Teacher-provided photos, videos, or files for this exact to-do. */
  attachments?: Array<{
    id: string;
    kind: 'image' | 'video' | 'file';
    url: string;
    name: string;
    contentType: string;
    storagePath: string;
  }>;
  /** Present when the dancer made this to-do from a class board post. */
  source?: {
    type: 'classPost';
    postId: string;
    offeringId: string;
    preview: string;
  };
}

/**
 * One family task scoped to this dancer, money included: Bobbie, 2026-08-14,
 * "Show everything." Amounts arrive inside title and description. The server
 * never sends assignees, sibling ids, or completion maps; `aboutMe` is the
 * only trace of task targeting and it is about this dancer alone.
 */
export interface DancerPortalTask {
  id: string;
  title: string;
  description: string;
  kind: 'general' | 'upload-music' | 'upload-file' | 'availability-poll'
    | 'choice-poll' | 'payment-needed' | 'costume-pickup' | 'timed-reminder';
  dueAt: string | null;
  done: boolean;
  doneAt: string | null;
  createdAt: string;
  createdByName: string | null;
  aboutMe: boolean;
}

export interface DancerPortalPolicy {
  id: string;
  kind: 'policy' | 'faq';
  title: string;
  body: string;
  sortIndex: number;
}

export interface DancerPortalCompBoardReply {
  id: string;
  /** Grown-up names are generic on purpose. See dancerSafeAuthor in lib/compBoard.mjs. */
  authorName: string;
  authorType: 'family' | 'studio' | 'dancer';
  text: string;
  createdAt: string;
  attachments?: Array<{
    id: string;
    kind: 'image' | 'video' | 'file';
    url: string;
    name: string;
    contentType: string;
    storagePath: string;
  }>;
}

export interface DancerPortalCompBoardPost {
  id: string;
  authorName: string;
  authorType: 'family' | 'studio' | 'dancer';
  body: string;
  createdAt: string;
  replyCount: number;
  replies: DancerPortalCompBoardReply[];
  attachments?: Array<{
    id: string;
    kind: 'image' | 'video' | 'file';
    url: string;
    name: string;
    contentType: string;
    storagePath: string;
  }>;
}

export interface DancerPortalCompBoard {
  member: boolean;
  posts: DancerPortalCompBoardPost[];
  hasMore: boolean;
  /**
   * True when the board could not be read this load, for example a composite
   * index still building right after a deploy. `member` is false and `posts`
   * is empty in this case too, because true membership was never resolved;
   * the UI shows a plain catching-up line rather than guessing at either.
   */
  boardUnavailable?: boolean;
}

/** Somebody an OLDER dancer can start a message with: their own currently
 * assigned teacher(s), unioned with the Director-curated parentContacts list
 * a parent already sees. See readDancerMessageTargets
 * (lib/dancerMessageTargets.mjs) - this is its exact server-computed answer,
 * never derived or filtered further on the client. */
export interface DancerPortalMessageTarget {
  uid: string;
  name: string;
  /** Why this person is reachable, shown under their name - e.g. "Teaches Tap 2". */
  reason: string;
}

/**
 * One message inside a dancer-to-staff conversation. `authorRole` stands in
 * for a resolved name on purpose: a guardian's own display name is never
 * stamped onto the thread (see dancerStaffThreads.mjs's file header for why),
 * so the dancer's own view categorizes by role rather than risking a stale
 * name.
 */
export interface DancerPortalStaffMessage {
  id: string;
  /** 'targetGuardian': a grown-up of the Jr Staff she is messaging, never her own. */
  authorRole: 'dancer' | 'staff' | 'guardian' | 'targetGuardian';
  text: string;
  createdAt: string;
  attachments?: Array<{
    id: string;
    kind: 'image' | 'video' | 'file';
    url: string;
    name: string;
    contentType: string;
    storagePath: string;
  }>;
}

/** One ongoing conversation between this dancer and one staff member, guardians included as full participants. */
export interface DancerPortalStaffThread {
  threadId: string;
  staffUid: string;
  staffName: string;
  /** On a Jr Staff thread: her first name, for "Ella's grown-up". */
  targetFirstName?: string;
  messages: DancerPortalStaffMessage[];
  hasMore: boolean;
}

/**
 * The dancer-to-staff messaging lane. Present ONLY for an OLDER dancer -
 * "Younger kids cannot [message a staff member]" (DECISIONS.md) is a missing
 * capability, not an empty inbox, the same reasoning jrStaff's own optional
 * field documents below.
 */
export interface DancerPortalStaffMessaging {
  /** Her teachers and published contacts. Everyone else loads on request. */
  targets: DancerPortalMessageTarget[];
  /** True when the rest of the staff can be loaded with getDancerPortalStaffPickerTargets. */
  allStaffAvailable?: boolean;
  threads: DancerPortalStaffThread[];
}

/** One class this dancer ASSISTS as Jr Staff, not one they are enrolled in. */
export interface DancerPortalAssistingClass {
  id: string;
  name: string;
  style: string;
  level: string;
  teacherName: string | null;
  /**
   * Which seat she is in on this class: she is named on its teaching team,
   * or she is one of its Jr assistants. Teaching wins when she is both, the
   * same tiebreak familyClassSet.ts uses. Optional for the same
   * cached-bundle reason as jrStaff.teacherTitle: the field arrived after
   * the lane itself, so an older server response can still omit it, and a
   * row without it reads as assisting the way it always did.
   */
  role?: 'teaching' | 'assisting';
  /**
   * The OTHER Jr assistants on this class, same family-safe shape as
   * DancerPortalClass above. The viewer's own uid is filtered out
   * server-side (readAssistingClasses in netlify/functions/dancerPortal.mjs):
   * this list is headed "the classes you assist", so the useful answer is
   * who else is on it. Optional for the same cached-bundle reason as
   * jrStaff.teacherTitle: the fields arrived after the lane itself, so an
   * older server response can still omit them.
   */
  assistantUids?: string[];
  assistantNames?: Record<string, string>;
  assistantDistinctUids?: string[];
  nextMeetings: DancerPortalEvent[];
}

/**
 * The Jr Staff lane. Present ONLY for a dancer the Director has designated Jr
 * Staff, so its absence is the honest answer for everyone else rather than an
 * empty section. An empty `assisting` inside a present block is a different
 * and equally real state: designated, but not attached to any class yet.
 *
 * This carries what a role='ensemble' Jr Staff account reads off Teach. It
 * does NOT carry the staff-attendance record, the sub board, or the teacher
 * title flow; see the PR that added this for why each is its own decision.
 */
export interface DancerPortalJrStaff {
  /**
   * NO LONGER SENT, and kept on the type only so an older cached response
   * still validates. Every class this list held is now a row in
   * `portal.classes` with a `role` on it (blendJrStaffClasses, in
   * netlify/functions/dancerPortal.mjs), and shipping the lane whole put each
   * taught class on the wire twice - its media and up to twenty meetings
   * each. Nothing on the client ever read it. Read `classes` instead.
   */
  assisting?: DancerPortalAssistingClass[];
  assistingHasMore: boolean;
  /**
   * This dancer's own Ms/Coach choice, or null when they have not picked one
   * yet. Optional on the type for the same cached-bundle reason as
   * `dancer.color`: the field arrived after jrStaff itself, so an older
   * server response can still omit it.
   */
  teacherTitle?: 'ms' | 'coach' | null;
}

/**
 * Verbatim from the design file's six category names, recorded in
 * TASK-QUEUE.md's Practice Tracker section (NOT the design source doc
 * itself: docs/design/LDG-Hub-Practice-Tracker.source.md only carries
 * `{{ f.label }}` template placeholders for this, and its own notes flag
 * that the WORKED ON labels may not even be the same six as the badge
 * categories). Mirrors netlify/functions/lib/practiceLog.mjs's copy of
 * this same list; do not add, remove, or reword a category here without a
 * source, that is exactly the invention this list exists to avoid.
 */
export const PRACTICE_FOCUS_CATEGORIES = [
  'CONSISTENCY', 'FLEXIBILITY', 'TURNS', 'JUMPS & TRICKS', 'STRENGTH', 'GROWTH MINDSET',
] as const;
export type PracticeFocusCategory = typeof PRACTICE_FOCUS_CATEGORIES[number];

export interface DancerPortalPracticeLog {
  id: string;
  minutes: number;
  date: string;
  focus: string[];
  note: string;
  createdAt: string;
}

/**
 * The dancer's own practice record. Optional on DancerPortalData for the
 * same cached-bundle reason `dancer.color` is: a browser holding an older
 * cached bundle can still be handed a response from a server that predates
 * this field. NOT present here: badges (badgeAwards does not exist yet -
 * see TASK-QUEUE.md) and the 8-week trend, which are follow-up packets, not
 * a missing field on this one.
 */
export interface DancerPortalPracticeSummary {
  recentLogs: DancerPortalPracticeLog[];
  recentLogsHasMore: boolean;
  streakDays: number;
  thisWeekMinutes: number;
  /**
   * True when this load's practice query failed server-side (a missing
   * composite index, most likely, before it deploys). streakDays and
   * thisWeekMinutes are 0 in that case too, but they are NOT a real zero -
   * they are the fallback shape, same reasoning as DancerPortalCompBoard's
   * own boardUnavailable above. A screen must show a catching-up state,
   * never render 0 as if it were a proven fact.
   */
  practiceUnavailable?: boolean;
}

/**
 * The six category keys straight from practiceBadges.mjs's
 * PRACTICE_BADGE_CATEGORIES (the server is the one and only source of the
 * catalog; this type only names the shape the server sends, it does not
 * duplicate any badge name or requirement text).
 */
export type PracticeBadgeCategoryKey = 'streak' | 'flex' | 'turns' | 'skills' | 'strength' | 'mindset';

/** One badge, merged with this dancer's earned status, exactly as
 * practiceBadgeAwards.mjs's readPracticeBadgeBoard returns it. */
export interface DancerPortalBadge {
  id: string;
  category: PracticeBadgeCategoryKey;
  name: string;
  requirement: string;
  auto: boolean;
  /** Packet J21: true when a Director has hidden this entry - it still
   * renders here (with its stored label) only because this dancer holds it.
   * Optional so a server payload built before this field existed still
   * matches the type. */
  hidden?: boolean;
  earned: boolean;
  /** 'class' added packet J22 part 3: a Yes on the roster pass check-off. */
  source: 'auto' | 'honor' | 'teacher' | 'class' | null;
  awardedAt: string | null;
  masteredAt: string | null;
  /**
   * Packet J22 part 3: present only for a class-sourced badge (source
   * 'class') - the real class, the real teacher or Jr Staff who marked it,
   * and the real date (DECISIONS.md entry 26, verbatim: "the real teacher
   * and class"). `className` and `markerName` are each null (never
   * invented) when the offering or the account has since been deleted;
   * `studioDate` is still real either way.
   */
  classSource?: { className: string | null; markerName: string | null; studioDate: string };
}

/**
 * Packet J22 part 3, dancer sign-in half: one earned skill, with the real
 * class it came from and the real date it was marked - already resolved to
 * words server side (see dancerPortal.mjs's own comment on why the dancer
 * portal resolves ids itself rather than a second Firebase call this
 * session has no token for).
 */
export interface DancerPortalInClassSkillYes {
  skillId: string;
  label: string;
  /**
   * Absent, never the invented placeholder word "Class" (Fable review, PR
   * #1113, SECOND round): "Keep the Yes" can name a class she has since
   * left or one that was truly deleted, and the server drops this field
   * rather than guess a name for it - the section renders skill and date
   * alone when it is missing.
   */
  className?: string;
  studioDate: string;
}

/** A skill still owed - label only. NEVER Close, Not yet, or a marker name. */
export interface DancerPortalInClassSkillPending {
  skillId: string;
  label: string;
}

/**
 * "In class skills", a DIFFERENT percentage from the Growth rubric - see
 * GrowthReport.tsx's InClassSkillsSection for the family/staff half of the
 * same feature. Optional: omitted entirely on a failed server-side read,
 * same hide-not-invent contract as `badges`.
 */
export interface DancerPortalInClassSkills {
  percentage: number;
  yesCount: number;
  distinctSkillCount: number;
  yes: DancerPortalInClassSkillYes[];
  stillWorkingOn: DancerPortalInClassSkillPending[];
}

export interface DancerPortalActiveChallenge {
  id: string;
  scope: 'class' | 'studio';
  start: string;
  end: string;
  weeklyGoalMinutes: number;
  createdAt: string | null;
}

export interface DancerPortalData {
  dancer: {
    id: string;
    firstName: string;
    /**
     * The dancer's own picked colour, by palette NAME, or null when they have
     * not been given one. Optional on the type on purpose: the field arrived
     * on 2026-08-16 and a browser holding a cached bundle can still be handed
     * a response from a function that predates it. `colorNameForStudentId`
     * turns either shape into a stable colour, so a missing field is a
     * different-looking page rather than a broken one.
     */
    color?: string | null;
    /**
     * Their own picture, set by a linked parent or by the dancer herself
     * (setDancerPortalPhoto), or null when there is none. Optional on the
     * type for the same cached-bundle reason `color` is.
     */
    photoUrl?: string | null;
  };
  experienceMode: DancerExperienceMode;
  /**
   * True when her parent turned her phone alerts off (DECISIONS 150).
   * Optional: an older server leaves it out, and the push lane still refuses.
   */
  phoneAlertsParentOff?: boolean;
  classes: DancerPortalClass[];
  upcomingEvents: DancerPortalEvent[];
  /**
   * The last studio day ('YYYY-MM-DD') through which `upcomingEvents` above
   * is PROVABLY COMPLETE - see computeScheduleHorizonDateKey in
   * dancerPortal.mjs, which is where this is actually decided. The month
   * grid (lib/dancerCalendar.ts's monthBeyondHorizon, DancerMonthCalendar)
   * uses this to tell a real free day from a day the studio has not posted
   * yet, day by day rather than only whole-month.
   *
   * OPTIONAL on the type on purpose, the same reason `dancer.color` is: this
   * field arrived after the server started sending it, and a browser holding
   * a cached bundle can still be handed a response from a function that
   * predates it. Every helper that reads it falls back to the OLD inferred,
   * events-derived horizon when it is absent, so an old cached payload
   * renders once with the old (month-granular) honesty rather than a broken
   * page.
   */
  scheduleHorizonDateKey?: string;
  /**
   * Kim's important dates, upcoming and inside their display window, filtered
   * to what this dancer may see. OMITTED entirely on a failed server read -
   * never an empty array, which would tell a dancer the studio has nothing
   * coming up. Same hide-not-invent contract as compBoard and practice.
   */
  countdowns?: DancerPortalCountdown[];
  /**
   * Open solo times she may see (DECISIONS 70, 73), already filtered by the
   * server's one audience rule. Absent when there are none for her. Read it
   * through parseOpenSoloCalendarPayload, never trusted raw.
   */
  openSoloTimes?: unknown;
  todos: DancerPortalTodo[];
  todosHasMore: boolean;
  tasks: DancerPortalTask[];
  tasksHasMore: boolean;
  policies: DancerPortalPolicy[];
  policiesHasMore: boolean;
  /**
   * The comp-team board. `member` is false for a dancer who is not on the
   * comp team, and the server sends no posts at all in that case, so there
   * is nothing here for a screen to leak by rendering the wrong branch.
   * Posting and replying are OLDER-only, through
   * postDancerPortalCompBoardMessage / replyDancerPortalToCompBoardPost
   * below: "Older dancers can post younger dancers, Read only and that's
   * true on all boards" (DECISIONS.md), named as binding on this board.
   */
  compBoard?: DancerPortalCompBoard;
  /**
   * Jr Staff, or absent. Optional in the same way and for the same reason as
   * compBoard: the server sends nothing at all for a dancer who is not Jr
   * Staff, so there is no branch a screen can render wrongly.
   */
  jrStaff?: DancerPortalJrStaff;
  practice?: DancerPortalPracticeSummary;
  /**
   * All 52 badges, merged with this dancer's earned status. Absent (not an
   * empty array) on a failed server-side read, same contract as compBoard's
   * own optionality above - a screen must hide the badge section entirely
   * rather than render a false "0 badges" for a live outage.
   */
  badges?: DancerPortalBadge[];
  /**
   * Packet J22 part 3. Absent (not a false 0%) on a failed server-side
   * read, same contract as badges above.
   */
  inClassSkills?: DancerPortalInClassSkills;
  /**
   * True only when the server tried to read her check-offs and failed, so a
   * screen says they are catching up instead of "nothing checked off yet".
   * Absent on a read that worked, including one with nothing in it.
   */
  inClassSkillsUnavailable?: true;
  /**
   * The single active staff-run challenge covering this dancer right now
   * (their own class, or the whole studio), or null when nothing is live.
   * Absent entirely (not null) on a failed server-side read - same
   * "hide, never invent a fact" contract the rest of this file uses,
   * though here the client-visible outcome (no banner) is the same either
   * way; see dancerPortal.mjs's own comment on why the distinction is kept
   * anyway.
   */
  activeChallenge?: DancerPortalActiveChallenge | null;
  /**
   * OLDER dancers only, absent entirely for a younger session. Sending goes
   * through sendDancerPortalStaffMessage below: one action for both a
   * brand-new conversation and a reply, since the thread id is deterministic
   * per (dancer, staff member) - "reply" is just "send again".
   */
  staffMessaging?: DancerPortalStaffMessaging;
  /**
   * Her OWN Done (Messages) and Read (Family to-dos) markers, Bobbie's two
   * asks of 2026-10-13. Cut by the server to the posts and to-dos this
   * payload shows her. Absent on a failed server read (and from a server that
   * predates it), which a screen treats as nothing marked yet: showing a
   * post as not Done is the safe direction. See DancerPortalInboxState.
   */
  inboxState?: DancerPortalInboxState;
  /**
   * Who wrote on her Big/Little board since she last opened it (DECISIONS
   * 135), keyed on the server by her studentId so a Jr Staff's two sign-ins
   * share it. Absent for a dancer with no group, on a failed read, and from
   * a server that predates it: no card, no count.
   */
  bigLittleUnread?: DancerPortalBigLittleUnread;
  /**
   * The best-effort lanes that FAILED this load and leave no other trace:
   * the Big/Little notes, the studio team lane (older view) and the Jr Staff
   * lane (the classes she teaches). Each of those otherwise reads the same
   * whether it failed or simply does not apply to her, so the kids' bar
   * could not tell a door or a class that failed to load from one she does
   * not have. Absent when none failed. Read only by the menu pins auto-clear
   * (Bobbie, 2026-10-31; lib/menuPinsSettled.ts), which takes no pin off on a
   * load that names any.
   */
  lanesUnavailable?: Array<'bigLittleUnread' | 'staffMessaging' | 'jrStaff'>;
  csrfToken: string;
}

/** One group with new notes for her (netlify/functions/lib/compPairDancerUnread.mjs). */
export interface DancerPortalBigLittleUnreadGroup {
  seasonId: string;
  groupId: string;
  /** New notes since she last opened this board, at most 99. */
  unread: number;
  /** When the newest one was written, in epoch milliseconds. */
  latestAtMs: number;
  /** Who wrote the newest one, as she reads it: "Ava (your Big)". */
  latestWho: string;
  /** The first words of the newest one, or '' when there are none to show. */
  latestWords: string;
}

export interface DancerPortalBigLittleUnread {
  version: number;
  total: number;
  /** Only groups with something new, newest first. */
  groups: DancerPortalBigLittleUnreadGroup[];
}

/**
 * A dancer's own markers, on her own sign-in only (netlify/functions/lib/
 * dancerInboxState.mjs). `version` goes up by one on every change, so the
 * client can keep whichever copy is newer: a portal load that started before
 * a tap can never undo it on arrival.
 */
export interface DancerPortalInboxState {
  version: number;
  /** Class posts she marked Done in her Messages door. */
  donePostIds: string[];
  /** Family to-dos she marked Read on her Family to-dos card. */
  readTaskIds: string[];
}

export class DancerSessionError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(message: string, status = 0, code = 'dancer_session_unavailable') {
    super(message);
    this.name = 'DancerSessionError';
    this.status = status;
    this.code = code;
  }
}

/**
 * THE PREVIEW CHOKE POINT (packet J13, Codex PR #1090 P1).
 *
 * Every dancer write in this app leaves through one of the POST helpers
 * below, so every one of them starts here. Inside a Director's read-only
 * preview this refuses BEFORE a connection is opened, announces itself once
 * so the preview shell can show its in-flow note, and throws a plain
 * DancerSessionError carrying those same words - which whichever section made
 * the call renders in its own error slot, saying "Not part of the preview"
 * rather than pretending the studio is down.
 *
 * status 0 on purpose: the 401 branch several callers key on means "your
 * session died, sign in again", and a preview refusal is not that.
 *
 * Outside a preview this is a single boolean read and nothing else changes.
 */
function refuseInDancerPreview(): void {
  if (blockedByDancerPreview()) {
    throw new DancerSessionError(
      `${DANCER_PREVIEW_BLOCKED_NOTE}.`,
      0,
      'dancer_preview_read_only',
    );
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

async function responsePayload(response: Response): Promise<Record<string, unknown>> {
  const value = await response.json().catch(() => ({}));
  return isRecord(value) ? value : {};
}

function friendlyError(payload: Record<string, unknown>, status: number): DancerSessionError {
  const code = typeof payload.error === 'string' ? payload.error : '';
  const messages: Record<string, string> = {
    invalid_credentials: 'That dancer username or password did not match.',
    login_failed: 'That dancer username or password did not match.',
    login_rate_limited: 'Too many sign-in tries. Wait a few minutes, then try again. No need to reset the password.',
    disabled: 'This dancer login is turned off. Ask your grown-up to turn it back on.',
    rate_limited: 'Too many tries. Take a short break, then try again.',
    session_expired: 'Your dancer session ended. Sign in again.',
    sign_in_required: 'Your dancer session ended. Sign in again.',
    csrf_invalid: 'Your secure session changed. Refresh and try again.',
    class_not_found: 'That class is no longer connected to your dancer login.',
    class_invalid: 'That class link does not look right.',
    guardian_required: 'Ask the studio to connect your parent or guardian before you post.',
    class_too_large: 'That class board needs the studio team to help with this message.',
    message_invalid: 'Write a message up to 2,000 characters, then try again.',
    attachment_not_ready: 'That attachment did not finish uploading. Choose it again and retry.',
    attachment_already_used: 'That attachment was already posted. Refresh and try again.',
    attachment_limit: 'Keep this to 4 attachments.',
    absence_invalid: 'Choose an upcoming class and an absence reason, then try again.',
    absence_older_only: 'Ask your grown-up to send this absence for you.',
    dancer_read_only: 'That one is for your grown-up to do.',
    photo_invalid: 'That photo did not come through. Choose it again.',
    event_not_found: 'That class time is no longer available. Refresh your dancer home.',
    todo_invalid: 'That to-do could not be saved. Refresh and try again.',
    costume_invalid: 'That costume mark did not save. Refresh and try again.',
    costume_not_found: 'That costume is no longer on your list. Refresh your dancer home.',
    tried_on_not_arrived: 'Tried on opens once your costume arrives.',
    tried_on_not_set: 'Tried on opens once the studio says who provides this costume.',
    piece_not_on_card: 'That piece is no longer on this costume card. Refresh your dancer home.',
    todo_not_found: 'That to-do is no longer on your list. Refresh your dancer home.',
    post_not_found: 'That class post is no longer available. Refresh your class board.',
    post_not_yours: 'You can only change your own posts.',
    post_already_removed: 'That post was already taken down. Refresh your class board.',
    post_acted_on: 'Someone has already answered this post, so its words stay as they are. You can still delete it.',
    post_invalid: 'That class post could not be found. Refresh your class board.',
    reaction_invalid: 'Pick one of the reactions shown, then try again.',
    post_outside_window: 'That post is more than a week old now, so it has left your Messages. It is still on your class board.',
    comp_board_not_found: 'That comp board post is no longer available. Refresh and try again.',
    staff_target_invalid: 'Choose someone from the list, then try again.',
    target_guardian_required: 'That Jr Staff member does not have a grown-up connected yet, so they cannot get messages here. Choose someone else.',
    request_conflict: 'Your earlier try may have already posted. Check the board, then send again if you still need to.',
    practice_log_invalid: 'Check the minutes and date, then try logging again.',
    badge_not_claimable: 'That badge is not one you claim yourself. Refresh and try again.',
    badge_award_conflict: 'That badge could not be claimed right now. Refresh and try again.',
    video_required: 'Choose a video file to upload here.',
    media_cap_reached: 'This class already has as many videos as it can hold. Ask the studio to clear out old ones first.',
    teacher_title_invalid: 'Choose Ms or Coach.',
    jr_staff_required: 'That choice is only for designated Jr Staff dancers.',
    teacher_name_missing: 'Add your name to your account before choosing a teacher title.',
    too_many_linked_classes: 'Too many linked classes to update safely right now. Ask the studio for help.',
    not_your_class: 'That class is not one you teach or help with any more. Refresh your dancer home.',
    not_jr_staff: 'That is only for designated Jr Staff dancers.',
    offering_invalid: 'That class link does not look right.',
    push_invalid: 'This phone could not be set up for alerts. Refresh the Hub and try again.',
    push_use_staff_alerts: 'Your Big/Little notes come with your Jr Staff alerts. Turn on phone alerts in Account on this phone.',
    push_phone_in_use: 'This phone already gets Hub alerts for someone else\'s account, so it stays theirs. Your new notes still show on your home. To get your own buzz, use your own phone or tablet.',
    push_parent_off: 'Your grown-up turned off phone alerts for you. Ask them if you want the buzz back.',
    push_token_rejected: 'This phone stopped taking Hub alerts. Turn notifications for the Hub off and on in your phone settings, then try again.',
    assessment_not_found: 'That assessment is not there any more. Refresh your dancer home.',
    assessment_invalid: 'That did not look right. Refresh your dancer home and try again.',
    event_not_available: 'That event is not on your calendar any more. All events shows what is coming up.',
    event_invalid: 'That event link does not look right. Open it again from All events.',
  };
  const fallback = status === 401
    ? 'That dancer username or password did not match.'
    : 'The dancer login could not connect. Try again.';
  return new DancerSessionError(messages[code] ?? fallback, status, code || 'dancer_session_unavailable');
}

function csrfHeaders(csrfToken?: string | null): HeadersInit {
  return {
    'Content-Type': 'application/json',
    ...(csrfToken ? { [DANCER_CSRF_HEADER]: csrfToken } : {}),
  };
}

function parseAuthStatus(payload: Record<string, unknown>): DancerAuthStatus {
  if (typeof payload.authenticated !== 'boolean') {
    throw new DancerSessionError('The dancer login returned an invalid response.');
  }
  const csrfToken = typeof payload.csrfToken === 'string' && payload.csrfToken.length > 0
    ? payload.csrfToken
    : null;
  if (payload.authenticated && !csrfToken) {
    throw new DancerSessionError('The dancer login returned an incomplete secure session.');
  }
  const jrStaffToken = typeof payload.jrStaffToken === 'string' && payload.jrStaffToken.length > 0
    ? payload.jrStaffToken
    : null;
  const reason = !payload.authenticated && payload.reason === 'session_ended'
    ? 'session_ended'
    : null;
  return { authenticated: payload.authenticated, csrfToken, jrStaffToken, reason };
}

export async function getDancerSessionStatus(): Promise<DancerAuthStatus> {
  // First opening must not wait forever on an uncacheable cookie check.
  // Bound the entire read, including the response body, without guessing
  // whether a cookie exists. The provider's existing error/retry path keeps
  // unknown sessions closed; no login, logout or write gains a retry here.
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => {
      reject(new DancerSessionError(
        'The account check took too long. Check your connection and try again.',
        0,
        'dancer_session_timeout',
      ));
      controller.abort();
    }, 10_000);
  });
  try {
    return await Promise.race([
      (async () => {
        const response = await fetch(DANCER_AUTH_ENDPOINT, {
          method: 'GET',
          credentials: 'include',
          cache: 'no-store',
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });
        const payload = await responsePayload(response);
        if (!response.ok) {
          // Signed out, but NOT "your sign-in ended" (PR #1116 refute round
          // one, P3-2). The dancerAuth GET never answers 401 itself: a
          // refused cookie comes back 200 with reason 'session_ended'. A 401
          // here is something in front of the Function, such as the staging
          // site's password gate, and says nothing about her dancer sign-in.
          if (response.status === 401) {
            return { authenticated: false, csrfToken: null, jrStaffToken: null, reason: null };
          }
          throw friendlyError(payload, response.status);
        }
        return parseAuthStatus(payload);
      })(),
      deadline,
    ]);
  } finally {
    clearTimeout(timer);
  }
}

export async function loginDancerSession(
  username: string,
  password: string,
): Promise<DancerAuthStatus> {
  const normalizedUsername = username.trim().toLowerCase();
  if (!normalizedUsername || normalizedUsername.includes('@')) {
    throw new DancerSessionError('Enter a dancer username without an email address.');
  }
  const response = await fetch(DANCER_AUTH_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: csrfHeaders(),
    body: JSON.stringify({ action: 'login', username: normalizedUsername, password }),
  });
  const payload = await responsePayload(response);
  if (!response.ok) throw friendlyError(payload, response.status);
  return parseAuthStatus(payload);
}

export async function logoutDancerSession(csrfToken: string): Promise<void> {
  const response = await fetch(DANCER_AUTH_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: csrfHeaders(csrfToken),
    body: JSON.stringify({ action: 'logout' }),
  });
  const payload = await responsePayload(response);
  if (!response.ok && response.status !== 401) throw friendlyError(payload, response.status);
}

/**
 * Absent is fine (an older server, or a deploy mid-flight). Present and the
 * wrong shape is not: the section below renders from this without re-checking,
 * so the guard is what makes the optional type honest.
 */
/**
 * Absent is the normal case for an older server, and the honest case for a
 * failed read. Present and the wrong shape is not: the section renders these
 * without re-checking, same contract as isCompBoardPayload below.
 */
function isCountdownsPayload(value: unknown): boolean {
  if (value === undefined) return true;
  return Array.isArray(value) && value.every((entry) => isRecord(entry)
    && typeof entry.id === 'string'
    && typeof entry.name === 'string'
    && typeof entry.date === 'string'
    && typeof entry.kind === 'string'
    && (entry.countdownEnabled === undefined || typeof entry.countdownEnabled === 'boolean'));
}

function isCompBoardPayload(value: unknown): boolean {
  if (value === undefined) return true;
  return isRecord(value)
    && typeof value.member === 'boolean'
    && Array.isArray(value.posts)
    && typeof value.hasMore === 'boolean'
    && (value.boardUnavailable === undefined || typeof value.boardUnavailable === 'boolean');
}

/**
 * Absent is the normal case (every dancer who is not Jr Staff). Present and
 * the wrong shape is not: the section renders from this without re-checking,
 * same contract as isCompBoardPayload above.
 */
function isJrStaffPayload(value: unknown): boolean {
  if (value === undefined) return true;
  return isRecord(value)
    // Optional since round 7: the field stopped being sent (see
    // DancerPortalJrStaff.assisting). Present and wrong is still refused, so
    // an older cached response is accepted and a malformed one is not.
    && (value.assisting === undefined || Array.isArray(value.assisting))
    && typeof value.assistingHasMore === 'boolean'
    && (value.teacherTitle === undefined || value.teacherTitle === null
      || value.teacherTitle === 'ms' || value.teacherTitle === 'coach');
}

/**
 * Absent is fine (an older server, or a deploy mid-flight - same reasoning
 * as isCompBoardPayload). Present and the wrong shape is not: the section
 * renders streakDays/thisWeekMinutes as plain numbers without re-checking,
 * so a malformed payload would otherwise crash there instead of failing
 * this guard first.
 */
function isPracticePayload(value: unknown): boolean {
  if (value === undefined) return true;
  return isRecord(value)
    && Array.isArray(value.recentLogs)
    && typeof value.recentLogsHasMore === 'boolean'
    && typeof value.streakDays === 'number'
    && typeof value.thisWeekMinutes === 'number'
    && (value.practiceUnavailable === undefined || typeof value.practiceUnavailable === 'boolean');
}

/**
 * Absent is fine (an older server, or a failed server-side read - see
 * DancerPortalBadge's own doc comment). Present and the wrong shape is not:
 * the badge board renders every entry's category/name/requirement/earned
 * without re-checking, same contract as isPracticePayload above.
 */
function isBadgesPayload(value: unknown): boolean {
  if (value === undefined) return true;
  return Array.isArray(value) && value.every((entry) => isRecord(entry)
    && typeof entry.id === 'string'
    && typeof entry.category === 'string'
    && typeof entry.name === 'string'
    && typeof entry.requirement === 'string'
    && typeof entry.auto === 'boolean'
    && typeof entry.earned === 'boolean'
    && (entry.source === null || typeof entry.source === 'string')
    && (entry.awardedAt === null || typeof entry.awardedAt === 'string')
    && (entry.masteredAt === null || typeof entry.masteredAt === 'string')
    && (entry.classSource === undefined || (isRecord(entry.classSource)
      && (entry.classSource.className === null || typeof entry.classSource.className === 'string')
      && (entry.classSource.markerName === null || typeof entry.classSource.markerName === 'string')
      && typeof entry.classSource.studioDate === 'string')));
}

/**
 * Absent (a server predating this field) and null (nothing active) are both
 * fine. Present and the wrong shape is not: the banner renders from this
 * without re-checking.
 */
function isActiveChallengePayload(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  return isRecord(value)
    && typeof value.id === 'string'
    && (value.scope === 'class' || value.scope === 'studio')
    && typeof value.start === 'string'
    && typeof value.end === 'string'
    && typeof value.weeklyGoalMinutes === 'number'
    && (value.createdAt === null || typeof value.createdAt === 'string');
}

/**
 * Absent is the normal case (a younger dancer, or an older server predating
 * this field). Present and the wrong shape is not: the section renders from
 * this without re-checking, same contract as isCompBoardPayload above.
 */
function isStaffMessagingPayload(value: unknown): boolean {
  if (value === undefined) return true;
  return isRecord(value)
    && Array.isArray(value.targets)
    && Array.isArray(value.threads);
}

/**
 * Absent is fine (an older server, or a failed server-side read). Present
 * and the wrong shape is not: InClassSkillsSection/DancerHome.tsx render
 * every entry's label/className/studioDate without re-checking.
 */
function isInClassSkillsPayload(value: unknown): boolean {
  if (value === undefined) return true;
  return isRecord(value)
    && typeof value.percentage === 'number'
    && typeof value.yesCount === 'number'
    && typeof value.distinctSkillCount === 'number'
    && Array.isArray(value.yes)
    && value.yes.every((entry) => isRecord(entry)
      && typeof entry.skillId === 'string'
      && typeof entry.label === 'string'
      // className is OMITTED, never null or any other placeholder, for a
      // Yes whose class the server could not name (left, or deleted) -
      // present and the wrong type is still refused, same as every other
      // field here.
      && (entry.className === undefined || typeof entry.className === 'string')
      && typeof entry.studioDate === 'string')
    && Array.isArray(value.stillWorkingOn)
    && value.stillWorkingOn.every((entry) => isRecord(entry)
      && typeof entry.skillId === 'string'
      && typeof entry.label === 'string');
}

/**
 * Absent is fine (a failed server read, or a server predating the field).
 * Present and the wrong shape is not: the Messages door and the Family to-dos
 * card both filter on these lists without re-checking.
 */
export function isDancerInboxStatePayload(value: unknown): value is DancerPortalInboxState | undefined {
  if (value === undefined) return true;
  return isRecord(value)
    && typeof value.version === 'number'
    && Number.isFinite(value.version)
    && value.version >= 0
    && Array.isArray(value.donePostIds)
    && value.donePostIds.every((id) => typeof id === 'string')
    && Array.isArray(value.readTaskIds)
    && value.readTaskIds.every((id) => typeof id === 'string');
}

/**
 * Absent is fine (no group, a failed read, or an older server). Present and
 * the wrong shape is not: the home card and the door count read it as is.
 */
export function isDancerBigLittleUnreadPayload(value: unknown): value is DancerPortalBigLittleUnread | undefined {
  if (value === undefined) return true;
  return isRecord(value)
    && typeof value.version === 'number'
    && typeof value.total === 'number'
    && Number.isFinite(value.total)
    && value.total >= 0
    && Array.isArray(value.groups)
    && value.groups.every((group) => isRecord(group)
      && typeof group.seasonId === 'string'
      && typeof group.groupId === 'string'
      && typeof group.unread === 'number'
      && Number.isFinite(group.unread)
      && typeof group.latestAtMs === 'number'
      && typeof group.latestWho === 'string'
      && typeof group.latestWords === 'string');
}

/**
 * Everything a portal payload must be EXCEPT its csrf token.
 *
 * Split out for the Director's read-only preview (packet J13,
 * lib/dancerHomePreview.ts). That payload is built by the same server
 * builder and validated by the same rules, but it deliberately carries NO
 * csrf token - the staff door strips the key so the response holds nothing
 * that could be handed back to a write. Sharing this core is what keeps the
 * two validators from drifting: a new required field lands in both at once.
 */
export function isDancerPortalCore(value: Record<string, unknown>): boolean {
  const dancer = value.dancer;
  return isRecord(dancer)
    && typeof dancer.id === 'string'
    && typeof dancer.firstName === 'string'
    && (value.experienceMode === 'younger' || value.experienceMode === 'older')
    && Array.isArray(value.classes)
    && Array.isArray(value.upcomingEvents)
    // Optional, not required - see the field's own comment on
    // DancerPortalData for why an absent value has to keep working.
    && (value.scheduleHorizonDateKey === undefined || typeof value.scheduleHorizonDateKey === 'string')
    && Array.isArray(value.todos)
    && typeof value.todosHasMore === 'boolean'
    && Array.isArray(value.tasks)
    && typeof value.tasksHasMore === 'boolean'
    && Array.isArray(value.policies)
    && typeof value.policiesHasMore === 'boolean'
    && isCountdownsPayload(value.countdowns)
    && isCompBoardPayload(value.compBoard)
    && isJrStaffPayload(value.jrStaff)
    && isPracticePayload(value.practice)
    && isBadgesPayload(value.badges)
    && isInClassSkillsPayload(value.inClassSkills)
    && (value.inClassSkillsUnavailable === undefined || value.inClassSkillsUnavailable === true)
    && isActiveChallengePayload(value.activeChallenge)
    && isStaffMessagingPayload(value.staffMessaging)
    && isDancerInboxStatePayload(value.inboxState)
    && isDancerBigLittleUnreadPayload(value.bigLittleUnread);
}

function isPortalData(value: Record<string, unknown>): value is Record<string, unknown> & DancerPortalData {
  return isDancerPortalCore(value)
    && typeof value.csrfToken === 'string'
    && (value.csrfToken as string).length > 0;
}

export async function getDancerPortal(): Promise<DancerPortalData> {
  refuseInDancerPreview();
  const response = await fetch(DANCER_PORTAL_ENDPOINT, {
    method: 'GET',
    credentials: 'include',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
  const payload = await responsePayload(response);
  if (!response.ok) throw friendlyError(payload, response.status);
  if (!isPortalData(payload)) {
    throw new DancerSessionError('Your dancer home returned an invalid response.');
  }
  return payload;
}

export async function setDancerPortalTodoDone(
  csrfToken: string,
  todoId: string,
  done: boolean,
): Promise<void> {
  refuseInDancerPreview();
  const response = await fetch(DANCER_PORTAL_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: csrfHeaders(csrfToken),
    body: JSON.stringify({ action: 'setTodoDone', todoId, done }),
  });
  const payload = await responsePayload(response);
  if (!response.ok) throw friendlyError(payload, response.status);
}

/**
 * A dancer picks their own colour, from the eight the palette allows.
 *
 * Bobbie, 2026-08-28: *"can we have a way for them to see their profile page?
 * Pick their color see that list?"* The server re-proves the session and
 * writes exactly `color` on exactly their own student record - see
 * setDancerColor in netlify/functions/dancerPortal.mjs. The name is validated
 * there against the same eight names the portal validates on the way out, so a
 * screen can never store a colour nobody can read.
 */
export async function setDancerPortalColor(
  csrfToken: string,
  color: string,
): Promise<{ color: string }> {
  refuseInDancerPreview();
  const response = await fetch(DANCER_PORTAL_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: csrfHeaders(csrfToken),
    body: JSON.stringify({ action: 'setMyColor', color }),
  });
  const payload = await responsePayload(response);
  if (!response.ok) throw friendlyError(payload, response.status);
  const value = isRecord(payload) ? payload : {};
  return { color: typeof value.color === 'string' ? value.color : color };
}

/**
 * HER PHONE ALERTS for Big/Little notes (DECISIONS 135). Three portal actions,
 * each re-proved against her session cookie and CSRF token on the server
 * (netlify/functions/lib/dancerSignInPush.mjs). The browser sends this
 * device's push token and nothing about who she is.
 */
async function dancerPushAction(
  csrfToken: string,
  body: { action: 'pushStatus' | 'registerPush' | 'removePush'; pushToken: string; claimId?: string },
): Promise<Record<string, unknown>> {
  refuseInDancerPreview();
  const response = await fetch(DANCER_PORTAL_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: csrfHeaders(csrfToken),
    body: JSON.stringify(body),
  });
  const payload = await responsePayload(response);
  if (!response.ok) throw friendlyError(payload, response.status);
  return payload;
}

/**
 * Where this phone stands for her, as the server sees it
 * (netlify/functions/lib/dancerSignInPush.mjs):
 *   on            her alerts are on here, in this sign-in;
 *   off           they are not, and she may turn them on;
 *   on_via_staff  her own Jr Staff account gets her Big/Little notes on this
 *                 phone, and she turned it on for her dancer home in this
 *                 sign-in, so her replies and to-dos buzz it too (DECISIONS 150);
 *   staff_phone   her own Jr Staff account gets her alerts on this phone, and
 *                 she has not turned it on for her dancer home here; a tap
 *                 adds her replies and to-dos;
 *   phone_in_use  a grown-up's account gets Hub alerts on this phone, and a
 *                 dancer tap never takes it from them;
 *   staff_alerts  this is a promoted Jr Staff sign-in: alerts live in Account.
 */
export type DancerPushServerState =
  | 'on' | 'off' | 'on_via_staff' | 'staff_phone' | 'phone_in_use' | 'staff_alerts' | 'parent_off';

const DANCER_PUSH_SERVER_STATES: readonly DancerPushServerState[] = [
  'on', 'off', 'on_via_staff', 'staff_phone', 'phone_in_use', 'staff_alerts', 'parent_off',
];

function pushServerState(payload: Record<string, unknown>, fallback: DancerPushServerState): DancerPushServerState {
  const state = payload.state;
  return typeof state === 'string' && (DANCER_PUSH_SERVER_STATES as readonly string[]).includes(state)
    ? state as DancerPushServerState
    : fallback;
}

/** Where this device's token stands for her, in this sign-in. */
export async function getDancerPushStatus(csrfToken: string, pushToken: string): Promise<DancerPushServerState> {
  const payload = await dancerPushAction(csrfToken, { action: 'pushStatus', pushToken });
  return pushServerState(payload, payload.registered === true ? 'on' : 'off');
}

/**
 * Turn her Big/Little phone alerts on for this device. 'on', or
 * 'on_via_staff' when her own Jr Staff account already has this phone. A
 * phone a grown-up's account has comes back as a DancerSessionError with
 * code 'push_phone_in_use', and a parent's off switch as 'push_parent_off'.
 */
export async function registerDancerPushDevice(
  csrfToken: string,
  pushToken: string,
  claimId: string,
): Promise<'on' | 'on_via_staff'> {
  const payload = await dancerPushAction(csrfToken, { action: 'registerPush', pushToken, claimId });
  return payload.state === 'on_via_staff' ? 'on_via_staff' : 'on';
}

/** Turn them off for this device. */
export async function removeDancerPushDevice(csrfToken: string, pushToken: string): Promise<void> {
  await dancerPushAction(csrfToken, { action: 'removePush', pushToken });
}

/** One costume's pickup for the signed-in dancer, as costumeStates returns it. */
export interface DancerCostumePickupState {
  state: NonNullable<DancerPortalCostume['pickupState']>;
  at: string | null;
  /** Her Tried on and pieces as the refresh read them; absent on an older server. */
  triedOnOpen?: boolean;
  inHand?: CostumeInHand;
}

const COSTUME_PICKUP_STATES = new Set(['picked-up', 'ready', 'not-ready', 'family-provides', 'check-with-studio']);

/**
 * A FRESH READING OF HER OWN COSTUME BAGS (2026-09-26). The dancer sign-in has
 * no live Firestore connection, so after the desk scans her bag the Costumes
 * tab would keep saying "Waiting for pickup" until the whole home reloaded.
 * This asks the server for just her pickup answers, keyed by costume id: the
 * same filter the portal load uses (approved, her grown-ups on the card, her
 * current classes), and never the class's pickups map. READ ONLY. Returns null
 * inside the Director's dancer preview, which holds no dancer session.
 */
export async function getDancerPortalCostumeStates(
  csrfToken: string,
): Promise<Record<string, DancerCostumePickupState> | null> {
  if (dancerPreviewActive()) return null;
  const response = await fetch(DANCER_PORTAL_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: csrfHeaders(csrfToken),
    body: JSON.stringify({ action: 'costumeStates' }),
  });
  const payload = await responsePayload(response);
  if (!response.ok) throw friendlyError(payload, response.status);
  const states = isRecord(payload) && isRecord(payload.states) ? payload.states : {};
  const out: Record<string, DancerCostumePickupState> = {};
  for (const [id, raw] of Object.entries(states)) {
    if (!isRecord(raw) || typeof raw.state !== 'string' || !COSTUME_PICKUP_STATES.has(raw.state)) continue;
    out[id] = {
      state: raw.state as DancerCostumePickupState['state'],
      at: typeof raw.at === 'string' ? raw.at : null,
      ...(typeof raw.triedOnOpen === 'boolean' ? { triedOnOpen: raw.triedOnOpen } : {}),
      ...(isRecord(raw.inHand) ? { inHand: inHandFrom(raw.inHand) } : {}),
    };
  }
  return out;
}

/**
 * SHE MARKS HER OWN COSTUME TRIED ON, OR A PIECE SHE HAS (2026-11-02). The
 * session says whose costume; the request names only the card, the kind of
 * mark, the piece and the value. Answers with her marks as they now stand.
 */
export async function setDancerPortalCostumeInHand(
  csrfToken: string,
  input: { costumeId: string; kind: 'triedOn' } | { costumeId: string; kind: 'have'; piece: string },
  value: boolean,
): Promise<CostumeInHand> {
  if (dancerPreviewActive()) throw new Error('The preview cannot mark costumes.');
  const response = await fetch(DANCER_PORTAL_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: csrfHeaders(csrfToken),
    body: JSON.stringify({ action: 'setCostumeInHand', ...input, value }),
  });
  const payload = await responsePayload(response);
  if (!response.ok) throw friendlyError(payload, response.status);
  return inHandFrom(isRecord(payload) ? payload.inHand : null);
}

/**
 * HER BAG GROUP, READ ONLY (packet BG-10; DECISIONS 123 item 17). The same
 * view her Jr Staff email account reads through bagGroupView.mjs: both doors
 * call one server resolver, and lib/bagGroupViewData.ts is the one reader of
 * it. Null inside the Director's dancer preview, which holds no session.
 *
 * KEPT FOR A MINUTE, per session token. Each answer costs her session read
 * budget (PORTAL_BAG_GROUP_VIEW_UNITS) and the resolver's lookups (an
 * indexed query of her family's groups plus a bounded scan of the active
 * ones, then each group's posts and items), and
 * her home remounts every time she comes back to it, so a minute's answer is
 * reused; the same window the Firebase door keeps (lib/bagGroupView.ts).
 * `fresh` (her Try again) always asks. A new sign-in has a new token, so a
 * cached view never crosses sessions.
 */
const BAG_VIEW_CACHE_MS = 60_000;
let bagViewCache: { token: string; at: number; askedAt: number; view: BagGroupView } | null = null;

export async function getDancerPortalBagGroupView(csrfToken: string, fresh = false): Promise<BagGroupView | null> {
  return (await getDancerPortalBagGroupViewAt(csrfToken, fresh))?.view ?? null;
}

/**
 * The same view with WHEN it was asked for (this device's clock), a kept one
 * included, for the kids' bar's menu pins auto-clear (lib/menuPinsSettled.ts).
 */
export async function getDancerPortalBagGroupViewAt(
  csrfToken: string,
  fresh = false,
): Promise<{ view: BagGroupView; askedAt: number } | null> {
  if (dancerPreviewActive()) return null;
  if (!fresh && bagViewCache && bagViewCache.token === csrfToken && Date.now() - bagViewCache.at < BAG_VIEW_CACHE_MS) {
    return { view: bagViewCache.view, askedAt: bagViewCache.askedAt };
  }
  const askedAt = Date.now();
  const response = await fetch(DANCER_PORTAL_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: csrfHeaders(csrfToken),
    body: JSON.stringify({ action: 'bagGroupView' }),
  });
  const payload = await responsePayload(response);
  if (!response.ok) throw friendlyError(payload, response.status);
  const view = parseBagGroupView(payload.view);
  bagViewCache = { token: csrfToken, at: Date.now(), askedAt, view };
  return { view, askedAt };
}

/**
 * ONE EVENT'S WHOLE PAGE, READ ONLY (Bobbie, 2026-09-16: "They also need to be
 * able to read and view the event pages. Not interact with them but view all
 * of the info and view those event pages"). A dancer session cannot read
 * countdownEvents, so the panels, photos, her call time and, on a competition
 * that is hers, the details, her lineup and the event wall come through the
 * portal's eventPage action. Null inside the Director's dancer preview, which
 * holds no session. A refusal (not hers, or gone) throws event_not_available.
 */
export async function getDancerPortalEventPage(
  csrfToken: string,
  eventId: string,
): Promise<DancerEventPage | null> {
  if (dancerPreviewActive()) return null;
  const response = await fetch(DANCER_PORTAL_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: csrfHeaders(csrfToken),
    body: JSON.stringify({ action: 'eventPage', eventId }),
  });
  const payload = await responsePayload(response);
  if (!response.ok) throw friendlyError(payload, response.status);
  const page = parseDancerEventPage(payload.page);
  if (!page) throw new DancerSessionError('The event did not load. Try again in a moment.', response.status, 'event_page_invalid');
  return page;
}

/**
 * THE TECHNIQUE FOR ONE CLASS SHE TEACHES OR ASSISTS (packet CX-8). A Jr Staff
 * on her dancer sign-in has no Firebase principal, so the guide's level for
 * her class comes through the dancer portal, the same answer her email
 * sign-in reads from the techniqueProgram Function (DECISIONS 26 and 27).
 * READ ONLY. Null inside the Director's dancer preview, which holds no
 * session; { matched: false } when the class is not tied to a level, and the
 * screen then shows nothing.
 */
export async function getDancerPortalClassTechnique(
  csrfToken: string,
  offeringId: string,
): Promise<ClassTechniqueResult | null> {
  if (dancerPreviewActive()) return null;
  const response = await fetch(DANCER_PORTAL_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: csrfHeaders(csrfToken),
    body: JSON.stringify({ action: 'classTechnique', offeringId }),
  });
  const payload = await responsePayload(response);
  if (!response.ok) throw friendlyError(payload, response.status);
  if (payload.matched !== true || !isRecord(payload.technique)) return { matched: false };
  const how = payload.how === 'tie' || payload.how === 'level-field' || payload.how === 'class-name'
    || payload.how === 'level-in-name' || payload.how === 'teacher-pick'
    ? payload.how
    : 'class-name';
  type Technique = Extract<ClassTechniqueResult, { matched: true }>['technique'];
  const techniques = Array.isArray(payload.techniques)
    ? payload.techniques.filter(isRecord) as unknown as Technique[]
    : [];
  return {
    matched: true,
    how,
    technique: payload.technique as unknown as Technique,
    ...(techniques.length > 1 ? { techniques } : {}),
    ...(payload.pending === true ? { pending: true } : {}),
  };
}

/**
 * Audit row N17: a class's formations, for a Jr Staff on her dancer sign-in,
 * the same answer her staff sign-in reads from the classFormations Function
 * (DECISIONS 26 and 27). READ ONLY. Null inside the Director's dancer
 * preview, which holds no session. The server refuses any dancer the class
 * does not seat as Jr Staff.
 */
export async function getDancerPortalClassFormations(
  csrfToken: string,
  offeringId: string,
): Promise<ClassFormationsView | null> {
  if (dancerPreviewActive()) return null;
  const response = await fetch(DANCER_PORTAL_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: csrfHeaders(csrfToken),
    body: JSON.stringify({ action: 'classFormations', offeringId }),
  });
  const payload = await responsePayload(response);
  if (!response.ok) throw friendlyError(payload, response.status);
  return formationsViewFrom(payload);
}

/**
 * Tried on and the family's pieces for a class she teaches or assists, for a
 * Jr Staff on her dancer sign-in: the same read-only panel her email sign-in
 * reads on the class page (DECISIONS 26 and 27). Null inside the Director's
 * dancer preview, which holds no session. The server refuses any dancer the
 * class does not seat as Jr Staff.
 */
export async function getDancerPortalClassInHand(
  csrfToken: string,
  offeringId: string,
): Promise<ClassInHandCostume[] | null> {
  if (dancerPreviewActive()) return null;
  const response = await fetch(DANCER_PORTAL_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: csrfHeaders(csrfToken),
    body: JSON.stringify({ action: 'classCostumeInHand', offeringId }),
  });
  const payload = await responsePayload(response);
  if (!response.ok) throw friendlyError(payload, response.status);
  return classInHandFromWire(payload);
}

/**
 * HER COSTUME TRACKER on her dancer sign-in (Bobbie, 2026-11-03): her own
 * costumes, one checklist, the same list her email Jr Staff account reads
 * through costumeTracker.mjs (DECISIONS 26 and 27). READ ONLY. Null inside
 * the Director's dancer preview, which holds no session.
 */
export async function getDancerPortalCostumeTracker(csrfToken: string): Promise<TrackerPayload | null> {
  if (dancerPreviewActive()) return null;
  const response = await fetch(DANCER_PORTAL_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: csrfHeaders(csrfToken),
    body: JSON.stringify({ action: 'costumeTracker' }),
  });
  const payload = await responsePayload(response);
  if (!response.ok) throw friendlyError(payload, response.status);
  return trackerFromWire(payload);
}

/**
 * A dancer sets her OWN profile picture, the twin of setDancerPortalColor
 * (packet S9; Bobbie, 2026-09-05: "Only the dancer and parents can change
 * their profile picture"). The bytes went up first through
 * uploadAttachmentViaGateway with target { kind: 'dancerPhoto' }; this hands
 * the server that reservation's id and nothing else. Which student, and
 * which URL, are both decided server-side from the live session and the
 * finalized reservation, so there is nothing here for a screen to get wrong.
 */
export async function setDancerPortalPhoto(
  csrfToken: string,
  attachmentReservationId: string,
): Promise<{ photoUrl: string }> {
  refuseInDancerPreview();
  const response = await fetch(DANCER_PORTAL_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: csrfHeaders(csrfToken),
    body: JSON.stringify({ action: 'setMyPhoto', attachmentReservationId }),
  });
  const payload = await responsePayload(response);
  if (!response.ok) throw friendlyError(payload, response.status);
  if (typeof payload.photoUrl !== 'string' || !payload.photoUrl) {
    throw new DancerSessionError('Your photo returned an invalid response.');
  }
  return { photoUrl: payload.photoUrl };
}

/**
 * A designated Jr Staff dancer chooses Ms or Coach, the dancer-login twin of
 * setMyTeacherTitle in lib/users.ts for a real staff account. The server
 * re-proves the Jr Staff designation itself, from the live session, before
 * any write - this only offers the door.
 */
export async function setDancerPortalTeacherTitle(
  csrfToken: string,
  title: 'ms' | 'coach',
): Promise<{ teacherName: string; updatedOfferings: number }> {
  refuseInDancerPreview();
  const response = await fetch(DANCER_PORTAL_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: csrfHeaders(csrfToken),
    body: JSON.stringify({ action: 'setTeacherTitle', title }),
  });
  const payload = await responsePayload(response);
  if (!response.ok) throw friendlyError(payload, response.status);
  if (typeof payload.teacherName !== 'string' || typeof payload.updatedOfferings !== 'number') {
    throw new DancerSessionError('Your teacher title returned an invalid response.');
  }
  return { teacherName: payload.teacherName, updatedOfferings: payload.updatedOfferings };
}

/**
 * A stable id for one submit attempt, generated once by the composer and
 * reused on retry so the server can tell a resend of a lost response apart
 * from a genuinely new post (Fable P2). Mirrors
 * newFamilyClassPostRequestId in lib/familyClassPosts.ts.
 */
export function newDancerPortalRequestId(): string {
  return globalThis.crypto?.randomUUID?.()
    ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * OLDER dancers only, server-enforced. Bobbie, 2026-08-14, the same night she
 * shut dancer posting entirely, split it back open by age: "Older kids
 * should be able to upload photos and send video" and, asked directly,
 * "Posts and messages both." A YOUNGER session's request is refused at the
 * server with dancer_read_only before any board read or write.
 *
 * requestId is required: pass the SAME id from state on a retry (generate a
 * fresh one only after the composer's own success), so a lost response can
 * be resent without duplicating the post or its notifications.
 */
export async function postDancerPortalClassMessage(
  csrfToken: string,
  offeringId: string,
  text: string,
  requestId: string,
  attachmentReservationIds: readonly string[] = [],
): Promise<{
  postId: string;
  guardianCount: number;
  recipientCount: number;
  /** REFUTE_walls_R2 finding 2: true when this post's video could not reach
   * its class's Music & videos because the shelf already held 200 entries.
   * The post itself still saved; only the shelf copy did not land. */
  videoMirrorBlocked: boolean;
  /** REFUTE_walls_R9 P3-4: how many of the post's own video attachments did
   * NOT land - a POST-level count, only meaningful when videoMirrorBlocked
   * is true. */
  videoMirrorBlockedCount: number;
  /** REFUTE_walls_R9 P3-4: true when SOME of the post's video landed and
   * some did not (a PARTIAL block); false means a TOTAL block, nothing
   * from this post reached the shelf. A caller must never word a partial
   * block like a total one, or suggest reposting - the clip already there
   * would land twice. */
  videoMirrorPartial: boolean;
  /** False on the gateway's REPLAY branch (the same post, a retry after an
   * earlier response was lost, P2-3) - true only for the write that first
   * created this post. REFUTE_walls_R7 P3-4: a caller reading
   * videoMirrorBlocked on a replay must not claim the shelf is full RIGHT
   * NOW, only that the clip did not land when this was posted. */
  created: boolean;
}> {
  refuseInDancerPreview();
  const response = await fetch(DANCER_PORTAL_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: csrfHeaders(csrfToken),
    body: JSON.stringify({
      action: 'postClassMessage',
      offeringId,
      text,
      requestId,
      ...(attachmentReservationIds.length ? { attachmentReservationIds } : {}),
    }),
  });
  const payload = await responsePayload(response);
  if (!response.ok) throw friendlyError(payload, response.status);
  if (typeof payload.postId !== 'string'
      || typeof payload.guardianCount !== 'number'
      || typeof payload.recipientCount !== 'number') {
    throw new DancerSessionError('The class board returned an invalid response.');
  }
  return {
    postId: payload.postId,
    guardianCount: payload.guardianCount,
    recipientCount: payload.recipientCount,
    videoMirrorBlocked: payload.videoMirrorBlocked === true,
    // REFUTE_walls_R9 P3-4: defaults to 1 (the original, single-clip
    // assumption every prior round made) when the field is missing.
    videoMirrorBlockedCount: typeof payload.videoMirrorBlockedCount === 'number' ? payload.videoMirrorBlockedCount : 1,
    videoMirrorPartial: payload.videoMirrorPartial === true,
    // Defaults to true (a fresh write) unless the server explicitly says
    // false, so an older server response missing this field is read as
    // the original, safer assumption every prior round already made.
    created: payload.created !== false,
  };
}

/**
 * FIX OR TAKE DOWN HER OWN CLASS BOARD MESSAGE (Bobbie, 2026-09-04: "We need
 * to set it so anyone can edit their own posts and delete their own posts").
 * Edit is for OLDER dancers only; delete is for either view (Bobbie,
 * 2026-10-26, her pick "Younger can delete": "Let a younger-view dancer
 * delete (not edit) her own old messages."). Server-enforced:
 * changeDancerClassMessage in
 * netlify/functions/lib/dancerClassBoard.mjs proves who she is from her live
 * session and runs the same transaction a teacher's or a parent's change
 * goes through, so the replaced wording is kept for the studio and a delete
 * takes the post off every family's board and clears its alerts.
 */
export async function changeDancerPortalClassMessage(
  csrfToken: string,
  postId: string,
  change: { kind: 'edit'; text: string } | { kind: 'retract' },
): Promise<{ postId: string; changed: boolean; warning: string | null }> {
  refuseInDancerPreview();
  const response = await fetch(DANCER_PORTAL_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: csrfHeaders(csrfToken),
    body: JSON.stringify(change.kind === 'edit'
      ? { action: 'editClassMessage', postId, text: change.text }
      : { action: 'retractClassMessage', postId }),
  });
  const payload = await responsePayload(response);
  if (!response.ok) throw friendlyError(payload, response.status);
  if (typeof payload.postId !== 'string' || typeof payload.changed !== 'boolean') {
    throw new DancerSessionError('Your class board returned an invalid response.');
  }
  return {
    postId: payload.postId,
    changed: payload.changed,
    warning: typeof payload.warning === 'string' ? payload.warning : null,
  };
}

/**
 * OLDER dancers only, server-enforced. Bobbie, 2026-08-18 night: "parents
 * and older dancers can upload in the class same place as teachers" -
 * uploads one already-finalized attachment (reserve/send/finalize happens
 * first through uploadAttachmentViaGateway with target
 * { kind: 'dancerClassMedia', offeringId }, same lifecycle
 * DancerClassMessageComposer already uses for a class-board attachment)
 * onto the class's own Music & Video tab. A YOUNGER session's request is
 * refused at the server with dancer_read_only before any offering read or
 * write.
 *
 * requestId is required, same idempotent-retry contract as
 * postDancerPortalClassMessage: pass the SAME id on a retry so a lost
 * response can be resent without a duplicate entry.
 */
export async function postDancerPortalClassVideo(
  csrfToken: string,
  offeringId: string,
  attachmentReservationId: string,
  requestId: string,
  label = '',
): Promise<{ videoUrl: string; created: boolean }> {
  refuseInDancerPreview();
  const response = await fetch(DANCER_PORTAL_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: csrfHeaders(csrfToken),
    body: JSON.stringify({
      action: 'addClassVideo',
      offeringId,
      attachmentReservationId,
      requestId,
      ...(label.trim() ? { label: label.trim() } : {}),
    }),
  });
  const payload = await responsePayload(response);
  if (!response.ok) throw friendlyError(payload, response.status);
  if (typeof payload.videoUrl !== 'string' || typeof payload.created !== 'boolean') {
    throw new DancerSessionError('Your video returned an invalid response.');
  }
  return { videoUrl: payload.videoUrl, created: payload.created };
}

/**
 * OLDER dancers only, server-enforced (Fable item 1, re-review of F1's
 * hub-staging merge). "Older dancers can post younger dancers, Read only
 * and that's true on all boards" (DECISIONS.md), named as binding on the
 * comp board specifically. requestId is required, same idempotent-retry
 * contract as postDancerPortalClassMessage.
 *
 * attachmentReservationIds ADDED 2026-08-17, same optional-and-empty-by-
 * default shape postDancerPortalClassMessage already uses above: the
 * standing full-parity ruling covers text AND media on every board, and
 * the comp board was the one board still missing the media half.
 */
export async function postDancerPortalCompBoardMessage(
  csrfToken: string,
  text: string,
  requestId: string,
  attachmentReservationIds: readonly string[] = [],
): Promise<{ postId: string }> {
  refuseInDancerPreview();
  const response = await fetch(DANCER_PORTAL_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: csrfHeaders(csrfToken),
    body: JSON.stringify({
      action: 'postCompBoardMessage',
      text,
      requestId,
      ...(attachmentReservationIds.length ? { attachmentReservationIds } : {}),
    }),
  });
  const payload = await responsePayload(response);
  if (!response.ok) throw friendlyError(payload, response.status);
  if (typeof payload.postId !== 'string') {
    throw new DancerSessionError('The comp board returned an invalid response.');
  }
  return { postId: payload.postId };
}

/**
 * OLDER dancers only, server-enforced. Same ruling and idempotent-retry
 * contract as postDancerPortalCompBoardMessage, attachments included.
 */
export async function replyDancerPortalToCompBoardPost(
  csrfToken: string,
  postId: string,
  text: string,
  requestId: string,
  attachmentReservationIds: readonly string[] = [],
): Promise<{ replyId: string }> {
  refuseInDancerPreview();
  const response = await fetch(DANCER_PORTAL_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: csrfHeaders(csrfToken),
    body: JSON.stringify({
      action: 'replyToCompBoardPost',
      postId,
      text,
      requestId,
      ...(attachmentReservationIds.length ? { attachmentReservationIds } : {}),
    }),
  });
  const payload = await responsePayload(response);
  if (!response.ok) throw friendlyError(payload, response.status);
  if (typeof payload.replyId !== 'string') {
    throw new DancerSessionError('The comp board returned an invalid response.');
  }
  return { replyId: payload.replyId };
}

/**
 * Message one staff member, OLDER dancers only, server-enforced. Also how a
 * dancer replies inside an ongoing conversation: the server thread id is
 * deterministic per (dancer, staffUid), so there is no separate reply
 * action - sending again to the same staffUid lands in the same thread. Their
 * guardian(s) are added as real thread participants who can reply too
 * (DECISIONS.md, "parents can reply", settled 2026-08-18). requestId is
 * required, same idempotent-retry contract as the comp board functions above.
 */
export async function sendDancerPortalStaffMessage(
  csrfToken: string,
  staffUid: string,
  text: string,
  requestId: string,
  attachmentReservationIds: readonly string[] = [],
): Promise<{ threadId: string; messageId: string }> {
  refuseInDancerPreview();
  const response = await fetch(DANCER_PORTAL_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: csrfHeaders(csrfToken),
    body: JSON.stringify({
      action: 'sendStaffMessage',
      staffUid,
      text,
      requestId,
      ...(attachmentReservationIds.length ? { attachmentReservationIds } : {}),
    }),
  });
  const payload = await responsePayload(response);
  if (!response.ok) throw friendlyError(payload, response.status);
  if (typeof payload.threadId !== 'string' || typeof payload.messageId !== 'string') {
    throw new DancerSessionError('Your message returned an invalid response.');
  }
  return { threadId: payload.threadId, messageId: payload.messageId };
}

/**
 * The all-staff picker list (N16), asked for when she opens it rather than
 * on every home load. READ ONLY. Null inside the Director's dancer preview,
 * which holds no dancer session. `complete` is false when the studio's staff
 * list was too long to send in full.
 */
export async function getDancerPortalStaffPickerTargets(
  csrfToken: string,
): Promise<{ targets: DancerPortalMessageTarget[]; complete: boolean } | null> {
  if (dancerPreviewActive()) return null;
  const response = await fetch(DANCER_PORTAL_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: csrfHeaders(csrfToken),
    body: JSON.stringify({ action: 'staffPickerTargets' }),
  });
  const payload = await responsePayload(response);
  if (!response.ok) throw friendlyError(payload, response.status);
  const raw = isRecord(payload) && Array.isArray(payload.targets) ? payload.targets : [];
  const targets = raw.flatMap((entry): DancerPortalMessageTarget[] => (
    isRecord(entry) && typeof entry.uid === 'string' && typeof entry.name === 'string'
      && typeof entry.reason === 'string'
      ? [{ uid: entry.uid, name: entry.name, reason: entry.reason }]
      : []
  ));
  return { targets, complete: !(isRecord(payload) && payload.complete === false) };
}

/**
 * Turn one readable class board post into the dancer's own PRIVATE to-do
 * (Bobbie, 2026-08-14: dancers get a to-do under their own student record,
 * never a staff-readable tasks/ doc). The server re-proves the dancer can
 * read the post; this client sends only the two ids.
 */
export async function addDancerPortalTodoFromPost(
  csrfToken: string,
  offeringId: string,
  postId: string,
): Promise<{ todoId: string; created: boolean }> {
  refuseInDancerPreview();
  const response = await fetch(DANCER_PORTAL_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: csrfHeaders(csrfToken),
    body: JSON.stringify({ action: 'addTodoFromPost', offeringId, postId }),
  });
  const payload = await responsePayload(response);
  if (!response.ok) throw friendlyError(payload, response.status);
  if (typeof payload.todoId !== 'string' || typeof payload.created !== 'boolean') {
    throw new DancerSessionError('Your to-do list returned an invalid response.');
  }
  return { todoId: payload.todoId, created: payload.created };
}

/**
 * A dancer, ANY experienceMode, sets or clears their own reaction on one
 * class board post (Bobbie, 2026-08-31: "not reply with words... it should
 * not could as the family response but... a like button or heart"). Pass
 * null to clear. The server re-proves the post is readable and writes ONLY
 * classPosts.dancerReactions - its OWN field, never the family/staff
 * `reactions` map, never an acknowledgement, never a reply - see
 * reactToDancerClassPost's own header in dancerPortal.mjs for the full
 * reasoning. Visible to family, staff, and other dancers (Bobbie,
 * 2026-08-31: "sure family can see, teachers can see, other students can
 * see"), rendered read-only on PostCard.tsx as well as the dancer's own
 * board - never a write surface for anyone but the reacting dancer.
 */
export async function reactToDancerPortalClassPost(
  csrfToken: string,
  offeringId: string,
  postId: string,
  emoji: string | null,
): Promise<{ postId: string; reaction: string | null }> {
  refuseInDancerPreview();
  const response = await fetch(DANCER_PORTAL_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: csrfHeaders(csrfToken),
    body: JSON.stringify({ action: 'reactToClassPost', offeringId, postId, emoji }),
  });
  const payload = await responsePayload(response);
  if (!response.ok) throw friendlyError(payload, response.status);
  if (typeof payload.postId !== 'string'
      || (payload.reaction !== null && typeof payload.reaction !== 'string')) {
    throw new DancerSessionError('Your reaction returned an invalid response.');
  }
  return { postId: payload.postId, reaction: payload.reaction as string | null };
}

function inboxStateFrom(payload: Record<string, unknown>): DancerPortalInboxState {
  const state = payload.inboxState;
  if (state === undefined || !isDancerInboxStatePayload(state)) {
    throw new DancerSessionError('Your Messages returned an invalid response.');
  }
  return state as DancerPortalInboxState;
}

/**
 * DONE, right there in her Messages door (Bobbie, 2026-10-13: "Kids view
 * messages need a way right there in the messages section to like with a
 * movie or mark as done with this"). Any experienceMode. `done: false` takes
 * the mark back. The server writes ONLY her own marker, never the post:
 * see setDancerClassPostDone in netlify/functions/dancerPortal.mjs. Answers
 * her whole marker state as it stands after the write.
 */
export async function setDancerPortalPostDone(
  csrfToken: string,
  offeringId: string,
  postId: string,
  done: boolean,
): Promise<DancerPortalInboxState> {
  refuseInDancerPreview();
  const response = await fetch(DANCER_PORTAL_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: csrfHeaders(csrfToken),
    body: JSON.stringify({ action: 'setPostDone', offeringId, postId, done }),
  });
  const payload = await responsePayload(response);
  if (!response.ok) throw friendlyError(payload, response.status);
  return inboxStateFrom(payload);
}

/**
 * MARK AS READ on her Family to-dos card (Bobbie, 2026-10-13: "Family to dos
 * kids should be able to mark as read"). Any experienceMode. `read: false`
 * takes the mark back. Her OWN marker only: the server never writes the
 * task, its done state, its recipients or anything about a payment (see
 * setDancerFamilyTodoRead in netlify/functions/dancerPortal.mjs).
 */
export async function setDancerPortalFamilyTodoRead(
  csrfToken: string,
  taskId: string,
  read: boolean,
): Promise<DancerPortalInboxState> {
  refuseInDancerPreview();
  const response = await fetch(DANCER_PORTAL_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: csrfHeaders(csrfToken),
    body: JSON.stringify({ action: 'setFamilyTodoRead', taskId, read }),
  });
  const payload = await responsePayload(response);
  if (!response.ok) throw friendlyError(payload, response.status);
  return inboxStateFrom(payload);
}


export async function submitDancerPortalAbsence(
  csrfToken: string,
  reservationId: string,
  reason: DancerAbsenceReason,
  note: string,
  attachmentReservationIds: readonly string[] = [],
): Promise<{ noticeId: string; date: string }> {
  refuseInDancerPreview();
  const response = await fetch(DANCER_PORTAL_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: csrfHeaders(csrfToken),
    body: JSON.stringify({
      action: 'submitAbsence', reservationId, reason, note,
      ...(attachmentReservationIds.length ? { attachmentReservationIds } : {}),
    }),
  });
  const payload = await responsePayload(response);
  if (!response.ok) throw friendlyError(payload, response.status);
  if (typeof payload.noticeId !== 'string' || typeof payload.date !== 'string') {
    throw new DancerSessionError('The absence confirmation returned an invalid response.');
  }
  return { noticeId: payload.noticeId, date: payload.date };
}

/**
 * Open to every dancer, younger and older alike (Bobbie, 2026-08-16,
 * DECISIONS.md "Practice logging is open to every dancer, younger and
 * older alike") - unlike absence, class messages, and comp board posts,
 * this is not gated to 'older'. requestId is required, same idempotent-
 * retry contract as the other portal writes: pass the SAME id from state
 * on a retry, a fresh one only after this call's own success.
 */
export async function postDancerPortalPracticeLog(
  csrfToken: string,
  minutes: number,
  date: string,
  focus: readonly string[],
  note: string,
  requestId: string,
): Promise<{ logId: string; created: boolean }> {
  refuseInDancerPreview();
  const response = await fetch(DANCER_PORTAL_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: csrfHeaders(csrfToken),
    body: JSON.stringify({ action: 'logPractice', minutes, date, focus, note, requestId }),
  });
  const payload = await responsePayload(response);
  if (!response.ok) throw friendlyError(payload, response.status);
  if (typeof payload.logId !== 'string' || typeof payload.created !== 'boolean') {
    throw new DancerSessionError('The practice log returned an invalid response.');
  }
  return { logId: payload.logId, created: payload.created };
}

/**
 * The "I LANDED IT" button. Honor badges only (an auto badge id is refused
 * server-side with badge_not_claimable - the consistency badges unlock from
 * logs, never from this endpoint). Idempotent: tapping an already-claimed
 * badge again returns `alreadyClaimed: true` rather than throwing, so a
 * retried tap after a lost response reads as a success, not a failure.
 */
export async function postDancerPortalClaimPracticeBadge(
  csrfToken: string,
  badgeId: string,
): Promise<{ badgeId: string; alreadyClaimed: boolean }> {
  refuseInDancerPreview();
  const response = await fetch(DANCER_PORTAL_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: csrfHeaders(csrfToken),
    body: JSON.stringify({ action: 'claimPracticeBadge', badgeId }),
  });
  const payload = await responsePayload(response);
  if (!response.ok) throw friendlyError(payload, response.status);
  if (typeof payload.badgeId !== 'string' || typeof payload.alreadyClaimed !== 'boolean') {
    throw new DancerSessionError('That badge claim returned an invalid response.');
  }
  return { badgeId: payload.badgeId, alreadyClaimed: payload.alreadyClaimed };
}

/**
 * HER ASSESSMENTS (DECISIONS 103 and 104): a teacher's one-on-one look at
 * her, newest first. Which dancer is decided by the live session, never by
 * the request. Empty inside the Director's dancer preview, which holds no
 * session.
 */
export async function getDancerPortalAssessments(csrfToken: string): Promise<AssessmentSummary[]> {
  if (dancerPreviewActive()) return [];
  const response = await fetch(DANCER_PORTAL_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: csrfHeaders(csrfToken),
    body: JSON.stringify({ action: 'assessments' }),
  });
  const payload = await responsePayload(response);
  if (!response.ok) throw friendlyError(payload, response.status);
  return Array.isArray(payload.assessments)
    ? payload.assessments.map(parseAssessmentSummary).filter((row): row is AssessmentSummary => row !== null)
    : [];
}

/** One of her assessments, with its replies. */
export async function getDancerPortalAssessment(csrfToken: string, assessmentId: string): Promise<AssessmentDetail> {
  const response = await fetch(DANCER_PORTAL_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: csrfHeaders(csrfToken),
    body: JSON.stringify({ action: 'assessment', assessmentId }),
  });
  const payload = await responsePayload(response);
  if (!response.ok) throw friendlyError(payload, response.status);
  const detail = parseAssessmentDetail(payload.assessment);
  if (!detail) throw new DancerSessionError('That assessment returned an invalid response.');
  return detail;
}

/** Her reply under one of her assessments. The teacher sees it. */
export async function replyDancerPortalAssessment(
  csrfToken: string,
  assessmentId: string,
  text: string,
  requestId: string,
): Promise<void> {
  refuseInDancerPreview();
  const response = await fetch(DANCER_PORTAL_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: csrfHeaders(csrfToken),
    body: JSON.stringify({ action: 'assessmentReply', assessmentId, text, requestId }),
  });
  const payload = await responsePayload(response);
  if (!response.ok) throw friendlyError(payload, response.status);
}
